import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Card, 
  CardContent, 
  Button, 
  Alert, 
  CircularProgress,
  Tabs,
  Tab,
  Chip,
  IconButton
} from '@mui/material';
import { 
  AccountBalance, 
  Payment, 
  Settings, 
  Dashboard,
  ArrowBack,
  Refresh
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { 
  createDashboardLink, 
  getAccountBalance,
  stripeConnectConfig 
} from '../stripeConnect';
import {
  EmbeddedAccountOnboarding,
  EmbeddedAccountManagement,
  EmbeddedPayments,
  EmbeddedPayouts,
  EmbeddedQuickSetup
} from '../components/stripe/EmbeddedComponents';
import config from '../config';

const StripeConnectSettings = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [accountId, setAccountId] = useState(null);
  const [accountStatus, setAccountStatus] = useState('pending');
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dashboardLoading, setDashboardLoading] = useState(false);
  const [userProfile, setUserProfile] = useState(null);

  // Fetch user profile and get their Stripe account ID
  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        if (!token) {
          throw new Error('No authentication token found');
        }

        const response = await fetch(`${config.API_URL}/profile`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (!response.ok) {
          throw new Error('Failed to fetch user profile');
        }

        const profileData = await response.json();
        setUserProfile(profileData);
        
        // Set the account ID from the user's profile
        if (profileData.stripe_account_id) {
          setAccountId(profileData.stripe_account_id);
          loadAccountData(profileData.stripe_account_id);
        } else {
          setError('No Stripe account found. Please connect your Stripe account first.');
          setLoading(false);
        }
      } catch (err) {
        console.error('Error fetching user profile:', err);
        setError('Failed to load user profile');
        setLoading(false);
      }
    };

    fetchUserProfile();
  }, []);

  const loadAccountData = async (accountId) => {
    try {
      setLoading(true);
      const balanceData = await getAccountBalance(accountId);
      setBalance(balanceData.balance);
      setAccountStatus('active'); // This would come from account data
      setError(null);
    } catch (err) {
      console.error('Error loading account data:', err);
      setError('Failed to load account information');
      setAccountStatus('incomplete');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDashboard = async () => {
    if (!accountId) return;
    
    try {
      setDashboardLoading(true);
      const dashboardData = await createDashboardLink(accountId);
      
      // Open dashboard in new tab
      window.open(dashboardData.dashboard.url, '_blank');
    } catch (err) {
      console.error('Error opening dashboard:', err);
      setError('Failed to open Stripe dashboard');
    } finally {
      setDashboardLoading(false);
    }
  };

  const handleRefresh = () => {
    if (accountId) {
      loadAccountData(accountId);
    }
  };

  const formatCurrency = (amount, currency = 'usd') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amount / 100);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'pending': return 'warning';
      case 'incomplete': return 'error';
      default: return 'default';
    }
  };

  const TabPanel = ({ children, value, index, ...other }) => (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`stripe-tabpanel-${index}`}
      aria-labelledby={`stripe-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 2 }}>
      {/* Header */}
      <Box display="flex" alignItems="center" mb={3}>
        <IconButton onClick={() => navigate(-1)} sx={{ mr: 2 }}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h4" component="h1" sx={{ flexGrow: 1 }}>
          Stripe Connect Settings
        </Typography>
        <IconButton onClick={handleRefresh} disabled={loading}>
          <Refresh />
        </IconButton>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Account Status Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">Account Status</Typography>
            <Chip 
              label={accountStatus.toUpperCase()} 
              color={getStatusColor(accountStatus)}
              variant="outlined"
            />
          </Box>
          
          {balance && (
            <Box display="flex" gap={4} mb={2}>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Available Balance
                </Typography>
                <Typography variant="h6">
                  {formatCurrency(balance.available?.[0]?.amount || 0, balance.available?.[0]?.currency)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Pending Balance
                </Typography>
                <Typography variant="h6">
                  {formatCurrency(balance.pending?.[0]?.amount || 0, balance.pending?.[0]?.currency)}
                </Typography>
              </Box>
            </Box>
          )}

          <Button
            variant="contained"
            startIcon={<Dashboard />}
            onClick={handleOpenDashboard}
            disabled={!accountId || dashboardLoading}
            sx={{ mt: 1 }}
          >
            {dashboardLoading ? 'Opening...' : 'Open Stripe Dashboard'}
          </Button>
        </CardContent>
      </Card>

      {/* Navigation Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
          <Tab 
            icon={<Settings />} 
            label="Account Setup" 
            iconPosition="start"
          />
          <Tab 
            icon={<Payment />} 
            label="Payments" 
            iconPosition="start"
          />
          <Tab 
            icon={<AccountBalance />} 
            label="Payouts" 
            iconPosition="start"
          />
        </Tabs>
      </Box>

      {/* Tab Panels using reusable components */}
      <TabPanel value={activeTab} index={0}>
        <Card>
          <CardContent>
            <EmbeddedQuickSetup
              accountId={accountId}
              accountStatus={accountStatus}
              onStatusChange={(newStatus) => {
                setAccountStatus(newStatus);
                // Refresh account data when status changes
                if (accountId) {
                  loadAccountData(accountId);
                }
              }}
              onLoadError={(error) => setError(error)}
              sx={{ minHeight: '500px' }}
            />
          </CardContent>
        </Card>
      </TabPanel>

      <TabPanel value={activeTab} index={1}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Payment History
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              View and manage your payment transactions.
            </Typography>
            
            <EmbeddedPayments
              accountId={accountId}
              onLoadError={(error) => setError('Failed to load payments interface')}
              sx={{ minHeight: '500px' }}
            />
          </CardContent>
        </Card>
      </TabPanel>

      <TabPanel value={activeTab} index={2}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Payout History
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Track your payouts and banking information.
            </Typography>
            
            <EmbeddedPayouts
              accountId={accountId}
              onLoadError={(error) => setError('Failed to load payouts interface')}
              sx={{ minHeight: '500px' }}
            />
          </CardContent>
        </Card>
      </TabPanel>
    </Container>
  );
};

export default StripeConnectSettings;